import { createElement, type ComponentType } from "react";
import { createBrowserRouter, Navigate, useLocation, useParams } from "react-router";
import Home from "./pages/Home";
import RootLayout from "./components/RootLayout";
import RouteErrorPage from "./pages/RouteErrorPage";
import NotFound from "./pages/NotFound";
import { withMemberAuth } from "./components/RequireMemberAuth";
import { withAuthenticatedAuth } from "./components/RequireAuthenticatedAuth";

// Every route below Home is code-split via react-router's `lazy` route field so a
// first-time visit to the homepage or a venue page (the SEO-critical entry points)
// doesn't ship the JS for the dashboard, checkout, chat, and 25+ other pages it
// will never touch. `Component: X` -> `lazy: () => import(...).then(...)`.
// RouteErrorPage and NotFound stay eager (imported above) since they must still
// render reliably if a chunk itself fails to load.
const lazyPage = (loader: () => Promise<{ default: ComponentType<any> }>) => ({
  lazy: async () => ({ Component: (await loader()).default }),
});
const lazyWrapped = (
  loader: () => Promise<{ default: ComponentType<any> }>,
  wrap: (C: ComponentType<any>) => ComponentType<any>
) => ({
  lazy: async () => ({ Component: wrap((await loader()).default) }),
});
const lazyNamed = <T extends Record<string, ComponentType<any>>>(
  loader: () => Promise<T>,
  key: keyof T
) => ({
  lazy: async () => ({ Component: (await loader())[key] }),
});

const RedirectToVenueForm = () => createElement(Navigate, { to: "/steps/venue-information", replace: true });
// Venue onboarding lives on venuelista.com; every /add-venue link/navigate lands here and leaves the SPA.
const ExternalAddVenue = () => {
  const { search, hash } = useLocation();
  window.location.replace(`https://venuelista.com/add-venue${search}${hash}`);
  return null;
};
const RedirectToAddVenue = ExternalAddVenue;
const ExternalPricing = () => {
  const { search, hash } = useLocation();
  window.location.replace(`https://venuelista.com/pricing${search}${hash}`);
  return null;
};
const RedirectToSignUp = () => {
  const { search } = useLocation();
  return createElement(Navigate, { to: `/sign-up${search}`, replace: true });
};
const LegacyUserProfileTicketRedirect = () => {
  const { slug = "", orderId = "" } = useParams();
  return createElement(Navigate, { to: `/member/tickets/${slug}/${orderId}/edit`, replace: true });
};
const LegacyUserProfilePaymentRedirect = () => {
  const { type = "", id = "" } = useParams();
  return createElement(Navigate, { to: `/member/payment/${type}/${id}`, replace: true });
};

export const router = createBrowserRouter([
  {
    path: "/",
    Component: RootLayout,
    errorElement: createElement(RouteErrorPage),
    children: [
      { index: true, Component: Home },
      // { path: "venues", ...lazyPage(() => import("./pages/Explore")) },
      { path: "venues", ...lazyPage(() => import("./pages/SearchVenues")) },
      { path: "search", ...lazyPage(() => import("./pages/SearchVenues")) },
      { path: "events", ...lazyPage(() => import("./pages/Events")) },
      { path: "services", ...lazyPage(() => import("./pages/Services")) },
      { path: "services/:id", ...lazyPage(() => import("./pages/ServiceDetail")) },
      { path: "explore", element: createElement(Navigate, { to: "/venues", replace: true }) },
      { path: "venue/:id/:tab", ...lazyPage(() => import("./pages/VenueLegacyRedirect")) },
      { path: "venue/:id", ...lazyPage(() => import("./pages/VenueLegacyRedirect")) },
      { path: "add-venue", Component: ExternalAddVenue },
      { path: "for-venues", Component: RedirectToAddVenue },
      { path: "addvenue", Component: RedirectToAddVenue },
      { path: "pricing", Component: ExternalPricing },
      { path: "sign-up", ...lazyPage(() => import("./pages/ForMembers")) },
      { path: "for-members", Component: RedirectToSignUp },
      { path: "login", ...lazyPage(() => import("./pages/Login")) },
      { path: "get-started", ...lazyPage(() => import("./pages/GetStarted")) },
      { path: "account-settings", ...lazyWrapped(() => import("./pages/AccountSettings"), withAuthenticatedAuth) },
      { path: "onboarding", ...lazyPage(() => import("./pages/MemberContinueRegister")) },
      { path: "member/continue-register", element: createElement(Navigate, { to: "/onboarding", replace: true }) },
      { path: "member", ...lazyWrapped(() => import("./pages/UserProfile"), withMemberAuth) },
      { path: "member/operations/:operation", ...lazyWrapped(() => import("./pages/UserProfile"), withMemberAuth) },
      { path: "member/friends", ...lazyWrapped(() => import("./pages/UserProfile"), withMemberAuth) },
      { path: "member/chat", ...lazyWrapped(() => import("./pages/UserProfile"), withMemberAuth) },
      { path: "member/photos", ...lazyWrapped(() => import("./pages/UserProfile"), withMemberAuth) },
      { path: "member/photo", element: createElement(Navigate, { to: "/member/photos", replace: true }) },
      { path: "member/friend", element: createElement(Navigate, { to: "/member/friends", replace: true }) },
      { path: "member/tickets/:slug/:orderId/edit", ...lazyWrapped(() => import("./pages/TicketOrderEdit"), withMemberAuth) },
      { path: "member/payment/:type/:id", ...lazyWrapped(() => import("./pages/ProfilePaymentCheckout"), withMemberAuth) },
      { path: "u/:name", ...lazyPage(() => import("./pages/UserProfile")) },
      { path: "u/:name/:section", ...lazyPage(() => import("./pages/UserProfile")) },
      { path: "user-profile", element: createElement(Navigate, { to: "/member", replace: true }) },
      { path: "user-profile/tickets/:slug/:orderId/edit", Component: LegacyUserProfileTicketRedirect },
      { path: "user-profile/payment/:type/:id", Component: LegacyUserProfilePaymentRedirect },
      { path: "membership-benefits", ...lazyPage(() => import("./pages/MembershipBenefits")) },
      { path: "invite-friends", ...lazyWrapped(() => import("./pages/InviteFriends"), withMemberAuth) },
      { path: "invite", element: createElement(Navigate, { to: "/invite-friends", replace: true }) },
      { path: "oauth/contact-import/callback", ...lazyPage(() => import("./pages/ContactImportCallback")) },
      { path: "oauth/instagram/callback", ...lazyPage(() => import("./pages/InstagramConnectCallback")) },
      // { path: "search", ...lazyPage(() => import("./pages/SearchVenues")) },
      { path: "search/map", ...lazyPage(() => import("./pages/MapViewSearch")) },
      { path: "chat", ...lazyWrapped(() => import("./pages/ConciergeMaximize"), withAuthenticatedAuth) },
      { path: "newsletter", element: createElement(Navigate, { to: "/", replace: true }) },
      { path: "contact", ...lazyPage(() => import("./pages/Contact")) },
      { path: "privacy", ...lazyNamed(() => import("./pages/Legal"), "PrivacyPolicy") },
      { path: "terms", ...lazyNamed(() => import("./pages/Legal"), "TermsOfService") },
      { path: "cookies", ...lazyNamed(() => import("./pages/Legal"), "CookiePolicy") },
      { path: "community-guidelines", ...lazyNamed(() => import("./pages/Legal"), "CommunityGuidelines") },
      { path: "venue-submission", Component: RedirectToVenueForm },
      { path: "venue-form/:step", ...lazyPage(() => import("./pages/VenueFormLegacyRedirect")) },
      { path: "venue-form", ...lazyPage(() => import("./pages/VenueFormLegacyRedirect")) },
      { path: "steps/:step", ...lazyPage(() => import("./pages/VenueSubmission")) },
      { path: "steps", Component: RedirectToVenueForm },
      { path: "venue-dashboard", ...lazyPage(() => import("./pages/VenueDashboard")) },
      { path: "verify-phone", ...lazyPage(() => import("./pages/PhoneVerification")) },
      { path: "verify-email", ...lazyPage(() => import("./pages/EmailVerification")) },
      { path: "verify", ...lazyPage(() => import("./pages/AccountVerify")) },
      { path: "forgot-password", ...lazyPage(() => import("./pages/ForgotPassword")) },
      { path: "reset-password/:token", ...lazyPage(() => import("./pages/ResetPassword")) },
      { path: "surprise-party/:token", ...lazyPage(() => import("./pages/SurprisePartyJoin")) },
      { path: "reset-password", element: createElement(Navigate, { to: "/forgot-password", replace: true }) },
      { path: "social-oauth/:provider/callback", ...lazyPage(() => import("./pages/OAuthCallback")) },
      { path: ":slug/bottle-checkout/:orderId", ...lazyPage(() => import("./pages/BottleCheckoutPage")) },
      { id: "venue-detail-event", path: ":slug/event/:eventSlug", ...lazyPage(() => import("./pages/VenueDetail")) },
      { id: "venue-detail-tab", path: ":slug/:tab", ...lazyPage(() => import("./pages/VenueDetail")) },
      { id: "venue-detail", path: ":slug", ...lazyPage(() => import("./pages/VenueDetail")) },
      { path: "*", Component: NotFound },
    ],
  },
]);
